import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { 
  FileText, Plus, Loader2, Send, Clock, CheckCircle2, XCircle, 
  AlertCircle, Trash2, Eye, Package
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ItemCodeRequestPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Form state
  const [genericName, setGenericName] = useState('');
  const [specs, setSpecs] = useState('');
  const [hasVariants, setHasVariants] = useState('no');
  const [variantFields, setVariantFields] = useState('');
  const [remarks, setRemarks] = useState('');

  const fetchRequests = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/api/item-code-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setRequests(data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const resetForm = () => {
    setGenericName('');
    setSpecs('');
    setHasVariants('no');
    setVariantFields('');
    setRemarks('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!genericName.trim()) {
      toast.error('Generic Name is required');
      return;
    }
    if (!specs.trim()) {
      toast.error('Specs are required');
      return;
    }
    if (hasVariants === 'yes' && !variantFields.trim()) {
      toast.error('Variant Fields are required when item has variants');
      return;
    }

    const token = localStorage.getItem('token');
    setSubmitting(true);

    try {
      const res = await fetch(`${API}/api/item-code-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          generic_name: genericName.trim(),
          specs: specs.trim(),
          has_variants: hasVariants === 'yes',
          variant_fields: hasVariants === 'yes' ? variantFields.trim() : null,
          remarks: remarks.trim() || null
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to submit');
      }

      toast.success('Item code request submitted successfully');
      resetForm();
      setShowForm(false);
      fetchRequests();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;

    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/api/item-code-requests/${requestId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to delete');
      }
      toast.success('Request deleted');
      fetchRequests();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200"><Package className="w-3 h-3 mr-1" />Completed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Force uppercase on input
  const handleUppercaseChange = (setter) => (e) => {
    setter(e.target.value.toUpperCase());
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="item-code-request-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-600" />
              Item Code Request
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Request new item codes for the Item Master
            </p>
          </div>
          {!showForm && (
            <Button onClick={() => setShowForm(true)} className="gap-2" data-testid="new-request-btn">
              <Plus className="w-4 h-4" />
              New Request
            </Button>
          )}
        </div>

        {/* Request Form */}
        {showForm && (
          <Card className="border-2 border-blue-200 bg-blue-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                New Item Code Request
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  All inputs must be in <strong>ALL CAPS</strong>. They will be auto-converted.
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="genericName" className="text-sm font-medium">
                      Generic Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="genericName"
                      value={genericName}
                      onChange={handleUppercaseChange(setGenericName)}
                      placeholder="E.G., MS PIPE, SS SHEET"
                      className="mt-1 uppercase"
                      data-testid="generic-name-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Base name without specs</p>
                  </div>

                  <div>
                    <Label className="text-sm font-medium">
                      Does it have variants? <span className="text-red-500">*</span>
                    </Label>
                    <RadioGroup 
                      value={hasVariants} 
                      onValueChange={setHasVariants}
                      className="flex gap-4 mt-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="no-variants" />
                        <Label htmlFor="no-variants" className="font-normal">No</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="yes-variants" />
                        <Label htmlFor="yes-variants" className="font-normal">Yes</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>

                <div>
                  <Label htmlFor="specs" className="text-sm font-medium">
                    Specifications <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="specs"
                    value={specs}
                    onChange={handleUppercaseChange(setSpecs)}
                    placeholder="E.G., 2 INCH DIA, 3MM THICK, GRADE 304"
                    className="mt-1 uppercase"
                    rows={3}
                    data-testid="specs-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">All specifications like size, thickness, grade, color etc.</p>
                </div>

                {hasVariants === 'yes' && (
                  <div>
                    <Label htmlFor="variantFields" className="text-sm font-medium">
                      Variant Fields <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="variantFields"
                      value={variantFields}
                      onChange={handleUppercaseChange(setVariantFields)}
                      placeholder="E.G., SIZE: 2FT, 3FT, 4FT&#10;COLOR: RED, BLUE, GREEN"
                      className="mt-1 uppercase"
                      rows={3}
                      data-testid="variant-fields-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">List all variant options (each variant on new line)</p>
                  </div>
                )}

                <div>
                  <Label htmlFor="remarks" className="text-sm font-medium">
                    Additional Remarks
                  </Label>
                  <Textarea
                    id="remarks"
                    value={remarks}
                    onChange={handleUppercaseChange(setRemarks)}
                    placeholder="ANY OTHER DETAILS..."
                    className="mt-1 uppercase"
                    rows={2}
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => { setShowForm(false); resetForm(); }}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="submit-request-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Submit Request
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Requests List */}
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">All Requests</h2>
          
          {requests.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">No requests yet</h3>
                <p className="text-sm text-gray-500">Create your first item code request</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {requests.map(request => (
                <Card key={request.id} className="border" data-testid={`request-card-${request.id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-sm text-gray-500">{request.request_number}</span>
                          {getStatusBadge(request.status)}
                        </div>
                        
                        <h3 className="font-semibold text-gray-900 mb-1">{request.generic_name}</h3>
                        
                        <div className="text-sm text-gray-600 space-y-1">
                          <p><span className="font-medium">Specs:</span> {request.specs}</p>
                          {request.has_variants && (
                            <p><span className="font-medium">Variants:</span> {request.variant_fields}</p>
                          )}
                          {request.remarks && (
                            <p><span className="font-medium">Remarks:</span> {request.remarks}</p>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                          <span>Requested by: {request.requested_by_name}</span>
                          <span>•</span>
                          <span>{new Date(request.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                        </div>

                        {request.admin_remarks && (
                          <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                            <span className="font-medium">Admin Remarks:</span> {request.admin_remarks}
                          </div>
                        )}

                        {request.created_item_code && (
                          <div className="mt-2 p-2 bg-green-50 rounded text-sm text-green-700">
                            <span className="font-medium">Created Item Code:</span> {request.created_item_code}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-1 ml-4">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedRequest(request)}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {request.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(request.id)}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* View Detail Modal */}
        {selectedRequest && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-semibold">Request Details</h3>
                <Button variant="ghost" size="sm" onClick={() => setSelectedRequest(null)}>✕</Button>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="font-mono text-sm text-gray-500">{selectedRequest.request_number}</span>
                  {getStatusBadge(selectedRequest.status)}
                </div>
                
                <div>
                  <Label className="text-xs text-gray-500">Generic Name</Label>
                  <p className="font-semibold">{selectedRequest.generic_name}</p>
                </div>
                
                <div>
                  <Label className="text-xs text-gray-500">Specifications</Label>
                  <p className="whitespace-pre-wrap">{selectedRequest.specs}</p>
                </div>
                
                <div>
                  <Label className="text-xs text-gray-500">Has Variants</Label>
                  <p>{selectedRequest.has_variants ? 'Yes' : 'No'}</p>
                </div>
                
                {selectedRequest.has_variants && selectedRequest.variant_fields && (
                  <div>
                    <Label className="text-xs text-gray-500">Variant Fields</Label>
                    <p className="whitespace-pre-wrap">{selectedRequest.variant_fields}</p>
                  </div>
                )}
                
                {selectedRequest.remarks && (
                  <div>
                    <Label className="text-xs text-gray-500">Remarks</Label>
                    <p className="whitespace-pre-wrap">{selectedRequest.remarks}</p>
                  </div>
                )}
                
                <div className="border-t pt-4 mt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-xs text-gray-500">Requested By</Label>
                      <p>{selectedRequest.requested_by_name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Request Date</Label>
                      <p>{new Date(selectedRequest.created_at).toLocaleDateString('en-IN')}</p>
                    </div>
                    {selectedRequest.processed_by_name && (
                      <>
                        <div>
                          <Label className="text-xs text-gray-500">Processed By</Label>
                          <p>{selectedRequest.processed_by_name}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Processed Date</Label>
                          <p>{selectedRequest.processed_at ? new Date(selectedRequest.processed_at).toLocaleDateString('en-IN') : '-'}</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {selectedRequest.admin_remarks && (
                  <div className="p-3 bg-gray-50 rounded">
                    <Label className="text-xs text-gray-500">Admin Remarks</Label>
                    <p>{selectedRequest.admin_remarks}</p>
                  </div>
                )}

                {selectedRequest.created_item_code && (
                  <div className="p-3 bg-green-50 rounded">
                    <Label className="text-xs text-gray-500">Created Item Code</Label>
                    <p className="font-mono font-semibold text-green-700">{selectedRequest.created_item_code}</p>
                  </div>
                )}
              </div>
              <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
                <Button variant="outline" onClick={() => setSelectedRequest(null)}>Close</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ItemCodeRequestPage;
